import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  makeStyles,
  RadioGroup,
  Theme,
  Typography,
} from '@material-ui/core';
import { useHistory, useParams } from 'react-router';

import moment from 'moment';
import Input from '../../../components/Input';
import CustomCheckbox from '../../../components/Checkbox';
import CustomRadio from '../../../components/Radio';
import { useAxios } from '../../../utils/hooks';
import { Form as FormModel } from '../../../models/form';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';
import Loader from '../../../components/Loader';
import getValidationErrors from '../../../utils/getValidationErrors';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    background: 'white',
    padding: theme.spacing(3),
    margin: theme.spacing(3),
    marginBottom: theme.spacing(10),
  },
  formTitles: {
    fontWeight: 'bolder',
  },
  formControl: {
    margin: theme.spacing(2, 0),
  },
  divider: {
    margin: theme.spacing(2),
  },
}));

const InitForm: React.FC = () => {
  const api = useAxios();
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormModel | null>(null);
  const { formId } = useParams<{ id: string; formId: string }>();

  const formRef = useRef<FormHandles>(null);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  useEffect(() => {
    async function getInitFormData() {
      try {
        const formResponse = await api.current?.get(`/forms/${formId}`);

        setForm(formResponse?.data);
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao carregar formulário.',
        });
      } finally {
        setLoading(false);
      }
    }

    getInitFormData();
  }, [api, formId]);

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        setLoading(true);
        setForm({ ...form, answers: data } as FormModel);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          observations: Yup.string(),
        });

        await schema.validate(data, { abortEarly: false });

        await api.current?.patch(`/forms/${formId}`, {
          answers: data,
        });

        setAlert({
          isActive: true,
          type: 'success',
          message: 'Ficha atualizada com sucesso.',
        });
      } catch (error: any) {
        setLoading(false);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao atualizar ficha.',
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [api, formId, form],
  );

  return (
    <>
      <Loader loading={loading} />

      {!loading && (
        <Form
          id="form"
          ref={formRef}
          initialData={form?.answers}
          onSubmit={handleSubmit}
          className={classes.root}
        >
          <FormControl fullWidth className={classes.formControl}>
            <Typography
              variant="h5"
              component="h1"
              align="center"
              className={classes.formTitles}
            >
              Ficha de Contato Inicial
            </Typography>

            <Typography
              variant="h6"
              component="h6"
              align="center"
              className={classes.formTitles}
            >
              (preenchida pelo paciente ou responsável)
            </Typography>

            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <FormLabel className={classes.formTitles}>
              DADOS DE IDENTIFICAÇÃO:
            </FormLabel>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  1 - DO PACIENTE:
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="patient_name"
                  label="Nome"
                  disabled
                  name="patient_name"
                />
              </Grid>

              <Grid item xs={4}>
                <Input
                  id="patient_gender"
                  label="Gênero"
                  disabled
                  name="patient_gender"
                />
              </Grid>

              <Grid item xs={4}>
                <Input
                  id="patient_race"
                  label="Raça/cor"
                  disabled
                  name="patient_race"
                />
              </Grid>

              <Grid item xs={2}>
                <Input
                  id="patient_birth"
                  label="Data de Nascimento"
                  placeholder="23/02/1997"
                  disabled
                  name="patient_birth"
                />
              </Grid>

              <Grid item xs={2}>
                <Input
                  id="patient_age"
                  label="Idade"
                  disabled
                  name="patient_age"
                />
              </Grid>
              {/*
              <Grid item xs={4}>
                <Input id="patient_rg" label="RG" disabled name="patient_rg" />
              </Grid> */}

              <Grid item xs={4}>
                <Input
                  id="patient_cpf"
                  label="CPF"
                  disabled
                  name="patient_cpf"
                />
              </Grid>
              {/*
              <Grid item xs={4}>
                <Input
                  id="patient_certidao"
                  label="Certidão de Nascimento"
                  disabled name="patient_certidao"
                />
              </Grid> */}

              <Grid item xs={12}>
                <Input
                  id="patient_birth_local"
                  label="Local de Nascimento"
                  disabled
                  name="patient_birth_local"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="patient_phone"
                  label="Telefone"
                  disabled
                  name="patient_phone"
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>ENDEREÇO:</FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="address_street"
                  label="Logradouro"
                  disabled
                  name="address_street"
                  placeholder="Rua/Av./Denominação/número/complemento"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="address_neighborhood"
                  label="Bairro"
                  disabled
                  name="address_neighborhood"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="address_city"
                  label="Cidade"
                  disabled
                  name="address_city"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="address_zipcode"
                  label="CEP"
                  disabled
                  name="address_zipcode"
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  ESCOLARIDADE:
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <RadioGroup row>
                  <CustomRadio
                    disabled
                    name="school_level"
                    options={[
                      {
                        id: '1',
                        value: 'kid_school',
                        label: 'Educação Infantil',
                      },
                      {
                        id: '2',
                        value: 'fundamental_school',
                        label: 'Ensino Fundamental',
                      },
                      {
                        id: '3',
                        value: 'middle_school',
                        label: 'Ensino Médio',
                      },
                      { id: '4', value: 'school_level_other', label: 'Outro' },
                    ]}
                  />

                  {/* <Input id="school_level" label="Outro" disabled name="school_level" /> */}
                </RadioGroup>
              </Grid>

              <Grid item xs={4}>
                <Input
                  id="school_year"
                  label="Ano"
                  disabled
                  name="school_year"
                />
              </Grid>

              <Grid item xs={4}>
                <Input
                  id="school_period"
                  label="Turno"
                  disabled
                  name="school_period"
                />
              </Grid>

              <Grid item xs={4}>
                <Input
                  id="school_fails"
                  label="Número de repetências"
                  disabled
                  name="school_fails"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="school_name"
                  label="Nome da Escola"
                  disabled
                  name="school_name"
                />
              </Grid>

              <Grid item xs={12}>
                <RadioGroup row>
                  <CustomRadio
                    disabled
                    name="school_type"
                    options={[
                      {
                        id: '1',
                        value: 'city_school',
                        label: 'Escola Municipal',
                      },
                      {
                        id: '2',
                        value: 'state_school',
                        label: 'Escola Estadual',
                      },
                      {
                        id: '3',
                        value: 'private_school',
                        label: 'Escola Privada',
                      },
                      { id: '4', value: 'school_level_other', label: 'Outro' },
                    ]}
                  />
                </RadioGroup>
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  2 - DOS RESPONSÁVEIS:
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Responsável 1
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="responsible_1_name"
                  label="Nome"
                  disabled
                  name="responsible_1_name"
                />
              </Grid>

              <Grid item xs={3}>
                <Input
                  id="responsible_1_rg"
                  label="RG"
                  disabled
                  name="responsible_1_rg"
                />
              </Grid>

              <Grid item xs={3}>
                <Input
                  id="responsible_1_cpf"
                  label="CPF"
                  disabled
                  name="responsible_1_cpf"
                />
              </Grid>

              <Grid item xs={3}>
                <Input
                  id="responsible_1_birth"
                  label="Data de Nascimento"
                  placeholder="23/02/1997"
                  disabled
                  name="responsible_1_birth"
                />
              </Grid>

              <Grid item xs={3}>
                <Input
                  id="responsible_1_race"
                  label="Raça/cor"
                  name="responsible_1_race"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="responsible_1_civil_situation"
                  label="Estado Civil"
                  name="responsible_1_civil_situation"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="responsible_1_guard_type"
                  label="Tipo de Guarda"
                  name="responsible_1_guard_type"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="responsible_1_phone_1"
                  label="Telefones"
                  disabled
                  name="responsible_1_phone_1"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="responsible_1_phone_2"
                  label="Telefones"
                  disabled
                  name="responsible_1_phone_2"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="responsible_1_email"
                  label="E-mail"
                  disabled
                  name="responsible_1_email"
                />
              </Grid>

              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Grau de parentesco com o paciente:
                </FormLabel>

                <RadioGroup row>
                  <CustomRadio
                    disabled
                    name="responsible_1_parent"
                    options={[
                      { id: '1', value: 'mother', label: 'Mãe' },
                      {
                        id: '2',
                        value: 'father',
                        label: 'Pai',
                      },
                      { id: '3', value: 'other', label: 'Outro' },
                    ]}
                  />

                  {/* <Input
                id="responsible_1_parent"
                label="Outro"
                disabled name="responsible_1_parent"
              /> */}
                </RadioGroup>
              </Grid>

              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Grau de Instrução:
                </FormLabel>

                <RadioGroup row>
                  <CustomRadio
                    disabled
                    name="responsible_1_school_level"
                    options={[
                      {
                        id: '1',
                        value: 'fundamental_school',
                        label: 'Ensino Fundamental',
                      },
                      {
                        id: '2',
                        value: 'middle_school',
                        label: 'Ensino Médio',
                      },
                      {
                        id: '3',
                        value: 'superior_school',
                        label: 'Ensino Superior',
                      },
                      { id: '4', value: 'other', label: 'Outro' },
                    ]}
                  />

                  {/* <Input
                id="responsible_1_school_level"
                label="Outro"
                disabled name="responsible_1_school_level"
              /> */}
                </RadioGroup>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="responsible_1_profession"
                  label="Profissão"
                  disabled
                  name="responsible_1_profession"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="responsible_1_income"
                  label="Renda (R$)"
                  disabled
                  name="responsible_1_income"
                />
              </Grid>

              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Responsável 2
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="responsible_2_name"
                  label="Nome"
                  disabled
                  name="responsible_2_name"
                />
              </Grid>

              <Grid item xs={3}>
                <Input
                  id="responsible_2_rg"
                  label="RG"
                  disabled
                  name="responsible_2_rg"
                />
              </Grid>

              <Grid item xs={3}>
                <Input
                  id="responsible_2_cpf"
                  label="CPF"
                  disabled
                  name="responsible_2_cpf"
                />
              </Grid>

              <Grid item xs={3}>
                <Input
                  id="responsible_2_birth"
                  label="Data de Nascimento"
                  placeholder="23/02/1997"
                  disabled
                  name="responsible_2_birth"
                />
              </Grid>

              <Grid item xs={3}>
                <Input
                  id="responsible_2_race"
                  label="Raça/cor"
                  name="responsible_2_race"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="responsible_2_civil_situation"
                  label="Estado Civil"
                  name="responsible_2_civil_situation"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="responsible_2_guard_type"
                  label="Tipo de Guarda"
                  name="responsible_2_guard_type"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="responsible_2_phone_1"
                  label="Telefones"
                  disabled
                  name="responsible_2_phone_1"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="responsible_2_phone_2"
                  label="Telefones"
                  disabled
                  name="responsible_2_phone_2"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="responsible_2_email"
                  label="E-mail"
                  disabled
                  name="responsible_2_email"
                />
              </Grid>

              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Grau de parentesco com o paciente:
                </FormLabel>

                <RadioGroup row>
                  <CustomRadio
                    disabled
                    name="responsible_2_parent"
                    options={[
                      { id: '1', value: 'mother', label: 'Mãe' },
                      {
                        id: '2',
                        value: 'father',
                        label: 'Pai',
                      },
                      { id: '3', value: 'other', label: 'Outro' },
                    ]}
                  />

                  {/* <Input
                id="responsible_2_parent"
                label="Outro"
                disabled name="responsible_2_parent"
              /> */}
                </RadioGroup>
              </Grid>

              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Grau de Instrução:
                </FormLabel>

                <RadioGroup row>
                  <CustomRadio
                    disabled
                    name="responsible_2_school_level"
                    options={[
                      {
                        id: '1',
                        value: 'fundamental_school',
                        label: 'Ensino Fundamental',
                      },
                      {
                        id: '2',
                        value: 'middle_school',
                        label: 'Ensino Médio',
                      },
                      {
                        id: '3',
                        value: 'superior_school',
                        label: 'Ensino Superior',
                      },
                      { id: '4', value: 'other', label: 'Outro' },
                    ]}
                  />

                  {/* <Input
                id="responsible_2_school_level"
                label="Outro"
                disabled name="responsible_2_school_level"
              /> */}
                </RadioGroup>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="responsible_2_profession"
                  label="Profissão"
                  disabled
                  name="responsible_2_profession"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="responsible_2_income"
                  label="Renda (R$)"
                  disabled
                  name="responsible_2_income"
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Responsável pelo custeio do tratamento:
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="payer_same"
                  label="Mesmos acima"
                  disabled
                  name="payer_same"
                />
                <Input
                  id="payer_other"
                  label="Outro"
                  disabled
                  name="payer_other"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="payer_parent"
                  label="Grau de parentesco com o paciente (se houver)"
                  disabled
                  name="payer_parent"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="payer_phone"
                  label="Telefone"
                  disabled
                  name="payer_phone"
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  id="payer_income"
                  label="Renda (R$)"
                  disabled
                  name="payer_income"
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  MOTIVO DA PROCURA AO CEAPIA:
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="motive"
                  label="Descreva brevemente quais são e quando começaram as questões que os trazem ao CEAPIA"
                  disabled
                  name="motive"
                  isMultiline
                  rows={5}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  RELAÇÃO DE TODAS AS PESSOAS QUE RESIDEM NA CASA:
                </FormLabel>
              </Grid>

              {/* 1) */}
              <Grid item xs={4}>
                <Input
                  id="resident_1_name"
                  label="Nome"
                  disabled
                  name="resident_1_name"
                />
              </Grid>
              <Grid item xs={2}>
                <Input
                  id="resident_1_age"
                  label="Idade"
                  disabled
                  name="resident_1_age"
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  id="resident_1_parent"
                  label="Parentesco"
                  disabled
                  name="resident_1_parent"
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  id="resident_1_ocupation"
                  label="Ocupação"
                  disabled
                  name="resident_1_ocupation"
                />
              </Grid>

              {/* 2) */}
              <Grid item xs={4}>
                <Input
                  id="resident_2_name"
                  label="Nome"
                  disabled
                  name="resident_2_name"
                />
              </Grid>
              <Grid item xs={2}>
                <Input
                  id="resident_2_age"
                  label="Idade"
                  disabled
                  name="resident_2_age"
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  id="resident_2_parent"
                  label="Parentesco"
                  disabled
                  name="resident_2_parent"
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  id="resident_2_ocupation"
                  label="Ocupação"
                  disabled
                  name="resident_2_ocupation"
                />
              </Grid>

              {/* 3) */}
              <Grid item xs={4}>
                <Input
                  id="resident_3_name"
                  label="Nome"
                  disabled
                  name="resident_3_name"
                />
              </Grid>
              <Grid item xs={2}>
                <Input
                  id="resident_3_age"
                  label="Idade"
                  disabled
                  name="resident_3_age"
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  id="resident_3_parent"
                  label="Parentesco"
                  disabled
                  name="resident_3_parent"
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  id="resident_3_ocupation"
                  label="Ocupação"
                  disabled
                  name="resident_3_ocupation"
                />
              </Grid>

              {/* 4) */}
              <Grid item xs={4}>
                <Input
                  id="resident_4_name"
                  label="Nome"
                  disabled
                  name="resident_4_name"
                />
              </Grid>
              <Grid item xs={2}>
                <Input
                  id="resident_4_age"
                  label="Idade"
                  disabled
                  name="resident_4_age"
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  id="resident_4_parent"
                  label="Parentesco"
                  disabled
                  name="resident_4_parent"
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  id="resident_4_ocupation"
                  label="Ocupação"
                  disabled
                  name="resident_4_ocupation"
                />
              </Grid>

              {/* 5) */}
              <Grid item xs={4}>
                <Input
                  id="resident_5_name"
                  label="Nome"
                  disabled
                  name="resident_5_name"
                />
              </Grid>
              <Grid item xs={2}>
                <Input
                  id="resident_5_age"
                  label="Idade"
                  disabled
                  name="resident_5_age"
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  id="resident_5_parent"
                  label="Parentesco"
                  disabled
                  name="resident_5_parent"
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  id="resident_5_ocupation"
                  label="Ocupação"
                  disabled
                  name="resident_5_ocupation"
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Como soube do CEAPIA?
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="know_school"
                  label="Escola. Qual?"
                  disabled
                  name="know_school"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="know_event"
                  label="Evento realizado no CEAPIA. Qual?"
                  disabled
                  name="know_event"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="know_social"
                  label="Facebook/Instagram/Site."
                  disabled
                  name="know_social"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="know_indication"
                  label="Teve indicação de alguém? Quem?"
                  disabled
                  name="know_indication"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="know_others"
                  label="Outros"
                  disabled
                  name="know_others"
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Já preencheu ficha ou realizou atendimento no Ceapia? (para o
                  paciente acima mencionado)
                </FormLabel>

                <RadioGroup row>
                  <CustomRadio
                    disabled
                    name="appointment"
                    options={[
                      { id: '1', value: 'yes', label: 'Sim' },
                      {
                        id: '2',
                        value: 'no',
                        label: 'Não',
                      },
                    ]}
                  />
                </RadioGroup>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="appointment_when"
                  label="Se sim, quando?"
                  disabled
                  name="appointment_when"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="appointment_who"
                  label="Com quem?"
                  disabled
                  name="appointment_who"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="appointment_familiar"
                  label="Já teve familiar em atendimento no CEAPIA? Quem:"
                  disabled
                  name="appointment_familiar"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="current_appointment_familiar"
                  label="Atualmente possui familiar em atendimento no CEAPIA? Quem:"
                  disabled
                  name="current_appointment_familiar"
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Observações:
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="observations"
                  label="Observações (uso interno)"
                  name="observations"
                  isMultiline
                  rows={5}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  TERMO DE AUTORIZAÇÃO:
                </FormLabel>
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                Eu
                <Input
                  id="term_responsible_name"
                  label="Nome do responsável"
                  disabled
                  name="term_responsible_name"
                />
              </Grid>

              <Grid item xs={12}>
                responsável por
                <Input
                  id="term_responsible_for"
                  label="Responsável por"
                  disabled
                  name="term_responsible_for"
                />
              </Grid>

              <Grid item xs={12}>
                estou ciente de que o CEAPIA é uma Instituição que visa o
                ensino, o atendimento e a pesquisa na infância e na adolescência
                e autorizo que os dados referentes ao atendimento possam ser
                utilizados na Instituição para fins de pesquisa. Outrossim,
                autorizo a Instituição a utilizar, sempre que julgar necessário,
                recursos técnicos nos atendimentos, tais como sala com espelho
                que permite visão unilateral, com auxílio de grupo técnico.
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                O CEAPIA compromete-se a manter sob estrito sigilo profissional
                as informações obtidas nos atendimentos. Desmarcações ou
                remarcações só serão aceitas quando comunicadas com até 72 horas
                de antecedência da data da triagem. Caso contrário, o valor não
                será reembolsado e se desejar marcar uma nova triagem será
                cobrado novamente.
              </Grid>

              <Grid item xs={12}>
                Declaro ainda estar ciente de que, o valor dos atendimentos é
                reajustado anualmente, no dia 01 de julho.
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                O NÃO COMPARECIMENTO NA TRIAGEM IMPLICARÁ NA ELIMINAÇÃO DESTES
                DOCUMENTOS.
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CustomCheckbox
                  disabled
                  name="agree"
                  value="agree"
                  label="Estou ciente e concordo com as informações deste termo."
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CustomCheckbox
                  disabled
                  name="term_consent"
                  value="term_consent"
                  label="Estou ciente e concordo com o Termo de Consentimento do CEAPIA."
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                Diretora de Atendimento
              </Grid>

              <Grid item xs={6}>
                Assinatura do responsável
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2} justify="flex-end">
              <Grid item xs={2}>
                <Input
                  id="form_data"
                  label="Data"
                  disabled
                  name="form_data"
                  defaultValue={moment().format('DD/MM/YYYY').toString()}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={1} justify="center">
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => history.goBack()}
                >
                  Voltar
                </Button>
              </Grid>

              <Grid item>
                <Button type="submit" color="primary" variant="contained">
                  Salvar
                </Button>
              </Grid>
            </Grid>
          </FormControl>
        </Form>
      )}

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default InitForm;
